import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

// Color palette for chart series
export const CHART_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b',
  '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
];

/**
 * Formats column names for better display in chart legends
 * @param {string} column - Raw column name
 * @returns {string} - Formatted label
 */
export const formatColumnLabel = (column) => {
  let label = column
    .replace(/_/g, ' ') // Replace underscores with spaces
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Add space between lowercase and uppercase
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2') // Add space between consecutive caps followed by lowercase
    .replace(/\b(USD|EUR|GBP|JPY|BTC|ETH|XAU|CL1|COM|CUR)\b/g, (match) => match) // Preserve currency/commodity codes
    .trim()
    .replace(/\s+/g, ' '); // Clean up multiple spaces
  
  // Capitalize first letter
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/**
 * Filters chart data based on date range
 * @param {Object} chartData - Original chart data
 * @param {Object} dateRange - Date range filter {from, to}
 * @returns {Object} - Filtered chart data
 */
export const filterChartDataByDateRange = (chartData, dateRange = null) => {
  if (!chartData || !chartData.categories || !chartData.dates) {
    return null;
  }

  // Filter data based on date range if provided
  let filteredData = chartData;
  if (dateRange && dateRange.from && dateRange.to) {
    const fromDate = new Date(dateRange.from);
    const toDate = new Date(dateRange.to);
    
    const filteredIndices = chartData.dates
      .map((dateStr, index) => ({ date: new Date(dateStr), index }))
      .filter(({ date }) => date >= fromDate && date <= toDate)
      .map(({ index }) => index);
    
    if (filteredIndices.length > 0) {
      filteredData = {
        ...chartData,
        dates: filteredIndices.map(i => chartData.dates[i]),
        data: filteredIndices.map(i => chartData.data[i])
      };
    }
  }

  return filteredData;
};

/**
 * Creates dataset configuration for a single data series
 * @param {string} column - Column name
 * @param {Array} filteredData - Filtered chart data
 * @param {number} colIndex - Column index for color selection
 * @returns {Object|null} - Dataset configuration or null if no valid data
 */
export const createDataset = (column, filteredData, colIndex) => {
  const data = filteredData.data.map((row, index) => ({
    x: filteredData.dates[index],
    y: row[column]
  })).filter(point => {
    // Filter out null/undefined, but keep zeros (they might be valid)
    // Only filter out NaN, not 0 values
    return point.y !== null && point.y !== undefined && !isNaN(point.y);
  });

  if (data.length === 0) return null;

  const label = formatColumnLabel(column);

  return {
    label: label,
    data: data,
    borderColor: CHART_COLORS[colIndex % CHART_COLORS.length],
    backgroundColor: CHART_COLORS[colIndex % CHART_COLORS.length] + '20',
    borderWidth: 2,
    fill: false,
    tension: 0.1,
    pointRadius: 0,          // Remove dots
    pointHoverRadius: 3,     // Small dot only on hover
    pointBackgroundColor: CHART_COLORS[colIndex % CHART_COLORS.length],
    pointBorderColor: CHART_COLORS[colIndex % CHART_COLORS.length],
    spanGaps: true           // Connect across missing data points (great for quarterly data)
  };
};

/**
 * Assigns individual Y-axis IDs to each dataset (similar to chartConfigBuilder approach)
 * Each dataset gets its own Y-axis for independent scaling
 * @param {Array} datasets - Array of dataset configurations
 * @param {Array} columns - Column names for generating unique axis IDs
 * @returns {Array} - Datasets with yAxisID assigned
 */
export const assignYAxes = (datasets, columns) => {
  // Assign each dataset its own Y-axis ID based on column name
  datasets.forEach((ds, i) => {
    const columnName = columns[i] || `series-${i}`;
    ds.yAxisID = `y-${columnName}`;
  });

  return datasets;
};

/**
 * Creates Y-axis configuration with all axes visible and properly labeled
 * Each dataset gets its own visible Y-axis with the formatted column name as the title
 * @param {Array} datasets - Datasets with yAxisID assigned
 * @param {Array} columns - Column names for generating axis configurations
 * @returns {Object} - Y-axis configuration object
 */
export const createYAxesConfig = (datasets, columns) => {
  const yAxes = {};

  // Create individual Y-axis for each dataset - all visible with proper labels
  datasets.forEach((ds, i) => {
    const columnName = columns[i] || `series-${i}`;
    const axisId = `y-${columnName}`;
    
    // Format the column name for display as axis title
    const axisTitle = formatColumnLabel(columnName);
    
    // Alternate positioning: left for even indices, right for odd indices
    const position = i % 2 === 0 ? 'left' : 'right';
    
    yAxes[axisId] = {
      type: 'linear',
      display: true, // Show all axes
      position: position,
      title: { 
        display: true, 
        text: axisTitle, // Use the formatted column name
        font: { size: 11, weight: 'bold' } 
      },
      grid: { 
        drawOnChartArea: i === 0, // Only first axis shows grid to avoid clutter
        color: i % 2 === 0 ? '#e0e0e0' : '#f0f0f0' // Slightly different grid colors
      },
      ticks: { 
        color: '#666', 
        font: { size: 10 } 
      }
    };
  });

  return yAxes;
};

/**
 * Creates a complete chart configuration object
 * @param {Array} datasets - Chart datasets
 * @param {string} categoryName - Name for chart title
 * @param {Array} columns - Column names for Y-axis generation
 * @returns {Object} - Complete Chart.js configuration
 */
export const createChartConfig = (datasets, categoryName, columns) => {
  const yAxes = createYAxesConfig(datasets, columns);

  return {
    type: 'line',
    data: {
      datasets: datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: categoryName,
          font: {
            size: 16,
            weight: 'bold'
          },
          padding: 20
        },
        legend: {
          position: 'bottom',
          labels: {
            font: {
              size: 12
            },
            usePointStyle: true,
            padding: 15
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        }
      },
      scales: {
        x: {
          type: 'time',
          time: {
            displayFormats: {
              day: 'MMM dd',
              month: 'MMM yyyy',
              year: 'yyyy'
            }
          },
          title: {
            display: true,
            text: 'Date',
            font: {
              size: 12,
              weight: 'bold'
            }
          }
        },
        ...yAxes
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    }
  };
};

/**
 * Main function to render charts for each category with date filtering
 * @param {Object} chartData - Raw chart data
 * @param {Object} dateRange - Date range filter {from, to}
 * @param {Object} styles - CSS module styles
 * @returns {Array|null} - Array of chart components or null
 */
export const renderCategoryCharts = (chartData, dateRange = null, styles = {}) => {
  const filteredData = filterChartDataByDateRange(chartData, dateRange);
  if (!filteredData) return null;

  return filteredData.categories.map((category, categoryIndex) => {
    // Prepare datasets for this category - show ALL columns, not just first 4
    const validDatasets = category.columns.map((column, colIndex) => {
      return createDataset(column, filteredData, colIndex);
    }).filter(dataset => dataset !== null);

    if (validDatasets.length === 0) return null;

    // Assign Y-axes using the chartConfigBuilder approach (individual axes)
    const datasetsWithAxes = assignYAxes(validDatasets, category.columns);

    // Create chart configuration with columns parameter
    const chartConfig = createChartConfig(datasetsWithAxes, category.name, category.columns);

    return (
      <div key={categoryIndex} className={styles.chartCategory} style={{ 
        height: '400px'
      }}>
        <Line {...chartConfig} />
      </div>
    );
  }).filter(chart => chart !== null);
};